import { Tooltip, TooltipContent, TooltipTrigger } from '@trellis/roots/tooltip'
import {
  describeReminderMarkLabel,
  describeReminderOffset,
  REMINDER_MAX_DAYS_AFTER_DUE,
  REMINDER_MAX_DAYS_BEFORE_DUE,
} from '@trellis/shared'
import type { ReminderStepInput, ReminderTimelineMark } from '@trellis/shared'
import { useRef, useState } from 'react'
import type { KeyboardEvent, PointerEvent } from 'react'

import { cn } from '@/lib/utils'

import { ReminderMarkDot, reminderMarkDotKind } from './ReminderMarkDot'
import {
  describeReminderOffsetSentence,
  ReminderMarkTooltipContent,
} from './ReminderMarkTooltipContent'

const RANGE_START = -REMINDER_MAX_DAYS_BEFORE_DUE - 3
const RANGE_END = REMINDER_MAX_DAYS_AFTER_DUE + 3

const position = (daysRelativeToDue: number) =>
  `${Math.min(100, Math.max(0, ((daysRelativeToDue - RANGE_START) / (RANGE_END - RANGE_START)) * 100))}%`

const clampDay = (day: number) =>
  Math.min(REMINDER_MAX_DAYS_AFTER_DUE, Math.max(-REMINDER_MAX_DAYS_BEFORE_DUE, day))

const isWithinBounds = (day: number) =>
  day >= -REMINDER_MAX_DAYS_BEFORE_DUE && day <= REMINDER_MAX_DAYS_AFTER_DUE

const isDayTaken = (steps: readonly ReminderStepInput[], index: number, day: number) =>
  steps.some((step, stepIndex) => stepIndex !== index && step.daysRelativeToDue === day)

/** First day past `from` in `direction` that no other step uses, or null at the boundary. */
function nextFreeDay(
  steps: readonly ReminderStepInput[],
  index: number,
  from: number,
  direction: 1 | -1,
): number | null {
  let day = from + direction
  while (isWithinBounds(day) && isDayTaken(steps, index, day)) day += direction
  return isWithinBounds(day) ? day : null
}

const TRACK_LIMITS = [
  {
    daysRelativeToDue: -REMINDER_MAX_DAYS_BEFORE_DUE,
    label: `Earliest reminder: ${REMINDER_MAX_DAYS_BEFORE_DUE} days before the due date`,
  },
  {
    daysRelativeToDue: REMINDER_MAX_DAYS_AFTER_DUE,
    label: `Latest reminder: ${REMINDER_MAX_DAYS_AFTER_DUE} days after the due date`,
  },
]

interface ReminderTimelineProps {
  marks: ReminderTimelineMark[]
  /**
   * Makes each step's dot a draggable slider bound to the form. Repeat dots stay fixed
   * because they follow the last email step.
   */
  editable?: {
    steps: readonly ReminderStepInput[]
    onStepDaysChange: (index: number, daysRelativeToDue: number) => void
  }
}

/**
 * Due-date-centred dots for each reminder step, with repeat dots hollow; hover a dot for
 * what it sends.
 */
export function ReminderTimeline({ marks, editable }: ReminderTimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null)
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const staticMarks = editable ? marks.filter((mark) => mark.isRepeat) : marks

  function moveStep(index: number, day: number | null) {
    if (!editable || day === null) return
    const step = editable.steps[index]
    if (day === step.daysRelativeToDue || isDayTaken(editable.steps, index, day)) return
    editable.onStepDaysChange(index, day)
  }

  function dayAtPointer(clientX: number): number {
    const track = trackRef.current!.getBoundingClientRect()
    const ratio = (clientX - track.left) / track.width
    return clampDay(Math.round(RANGE_START + ratio * (RANGE_END - RANGE_START)))
  }

  function handlePointerDown(index: number, pointerEvent: PointerEvent<HTMLDivElement>) {
    if (pointerEvent.button !== 0) return
    pointerEvent.preventDefault()
    pointerEvent.currentTarget.setPointerCapture(pointerEvent.pointerId)
    pointerEvent.currentTarget.focus()
    setDraggingIndex(index)
  }

  function handlePointerMove(index: number, pointerEvent: PointerEvent<HTMLDivElement>) {
    if (draggingIndex !== index) return
    moveStep(index, dayAtPointer(pointerEvent.clientX))
  }

  function handleKeyDown(index: number, keyboardEvent: KeyboardEvent<HTMLDivElement>) {
    if (!editable) return
    const currentDay = editable.steps[index].daysRelativeToDue
    const targets: Record<string, () => number | null> = {
      ArrowLeft: () => nextFreeDay(editable.steps, index, currentDay, -1),
      ArrowDown: () => nextFreeDay(editable.steps, index, currentDay, -1),
      ArrowRight: () => nextFreeDay(editable.steps, index, currentDay, 1),
      ArrowUp: () => nextFreeDay(editable.steps, index, currentDay, 1),
      Home: () =>
        nextFreeDay(editable.steps, index, -REMINDER_MAX_DAYS_BEFORE_DUE - 1, 1),
      End: () => nextFreeDay(editable.steps, index, REMINDER_MAX_DAYS_AFTER_DUE + 1, -1),
    }
    const target = targets[keyboardEvent.key]
    if (!target) return
    keyboardEvent.preventDefault()
    moveStep(index, target())
  }

  return (
    <div>
      <div ref={trackRef} className="relative mx-3 mt-1.5 h-18.5 select-none">
        <div
          className="border-border absolute top-7 left-0 border-t-2 border-dashed opacity-60"
          style={{ width: position(-REMINDER_MAX_DAYS_BEFORE_DUE) }}
          aria-hidden="true"
        />
        <div
          className="border-border absolute top-7 right-0 border-t-2 border-dashed opacity-60"
          style={{ left: position(REMINDER_MAX_DAYS_AFTER_DUE) }}
          aria-hidden="true"
        />
        <div
          className="bg-border absolute top-7 h-0.5"
          style={{
            left: position(-REMINDER_MAX_DAYS_BEFORE_DUE),
            right: `calc(100% - ${position(REMINDER_MAX_DAYS_AFTER_DUE)})`,
          }}
          aria-hidden="true"
        />
        {TRACK_LIMITS.map((limit) => (
          <Tooltip key={limit.daysRelativeToDue}>
            <TooltipTrigger asChild>
              <span
                className="group/limit absolute top-5 -ml-1.5 flex h-4.5 w-3 justify-center"
                style={{ left: position(limit.daysRelativeToDue) }}
                aria-hidden="true"
              >
                <span className="bg-muted-foreground/50 group-hover/limit:bg-muted-foreground h-full w-0.5 rounded-full transition-colors" />
              </span>
            </TooltipTrigger>
            <TooltipContent>{limit.label}</TooltipContent>
          </Tooltip>
        ))}
        <div
          className="bg-foreground border-background absolute top-4 -ml-3.25 size-6.5 rounded-full border-2"
          style={{ left: position(0) }}
          aria-hidden="true"
        />
        <div
          className="absolute top-0 -translate-x-1/2 text-[11px] font-semibold whitespace-nowrap"
          style={{ left: position(0) }}
          aria-hidden="true"
        >
          Due date
        </div>
        {staticMarks.map((mark, index) => {
          const kind = reminderMarkDotKind(mark)
          return (
            <div key={index} aria-hidden="true">
              {mark.daysRelativeToDue !== 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="absolute top-3.75 -ml-3.5 flex"
                      style={{ left: position(mark.daysRelativeToDue) }}
                    >
                      <ReminderMarkDot kind={kind} />
                    </span>
                  </TooltipTrigger>
                  <ReminderMarkTooltipContent
                    kind={kind}
                    detail={describeReminderOffsetSentence(mark.daysRelativeToDue)}
                  />
                </Tooltip>
              )}
              <div
                className={cn(
                  'text-muted-foreground absolute top-11 -translate-x-1/2 text-[11px] whitespace-nowrap tabular-nums',
                  mark.daysRelativeToDue === 0 && 'text-foreground font-semibold',
                )}
                style={{ left: position(mark.daysRelativeToDue) }}
              >
                {mark.label}
              </div>
            </div>
          )
        })}
        {editable?.steps.map((step, index) => {
          const isDragging = draggingIndex === index
          const kind = reminderMarkDotKind({ ...step, isRepeat: false })
          return (
            <Tooltip
              key={index}
              touch="none"
              open={hoveredIndex === index && draggingIndex === null}
              onOpenChange={(isOpen) => setHoveredIndex(isOpen ? index : null)}
            >
              <TooltipTrigger asChild>
                <div
                  role="slider"
                  tabIndex={0}
                  aria-label={`Step ${index + 1} day`}
                  aria-orientation="horizontal"
                  aria-valuemin={-REMINDER_MAX_DAYS_BEFORE_DUE}
                  aria-valuemax={REMINDER_MAX_DAYS_AFTER_DUE}
                  aria-valuenow={step.daysRelativeToDue}
                  aria-valuetext={describeReminderOffset(step.daysRelativeToDue)}
                  className={cn(
                    'group absolute top-3.25 -ml-4 flex size-8 touch-none items-center justify-center rounded-full outline-none',
                    isDragging ? 'z-20 cursor-grabbing' : 'cursor-grab hover:z-10',
                  )}
                  style={{ left: position(step.daysRelativeToDue) }}
                  onPointerDown={(pointerEvent) => handlePointerDown(index, pointerEvent)}
                  onPointerMove={(pointerEvent) => handlePointerMove(index, pointerEvent)}
                  onPointerUp={() => setDraggingIndex(null)}
                  onPointerCancel={() => setDraggingIndex(null)}
                  onKeyDown={(keyboardEvent) => handleKeyDown(index, keyboardEvent)}
                >
                  <ReminderMarkDot
                    kind={kind}
                    className={cn(
                      'transition-[scale,box-shadow] group-hover:scale-115 group-hover:ring-4 group-focus-visible:ring-2',
                      step.emailClient
                        ? 'ring-sky-500/25 group-focus-visible:ring-sky-500'
                        : 'ring-amber-500/25 group-focus-visible:ring-amber-500',
                      isDragging && 'scale-115 ring-4',
                    )}
                  />
                  <span
                    className={cn(
                      'text-muted-foreground group-hover:text-foreground absolute top-7.75 left-1/2 -translate-x-1/2 text-[11px] whitespace-nowrap tabular-nums',
                      (step.daysRelativeToDue === 0 || isDragging) &&
                        'text-foreground font-semibold',
                    )}
                    aria-hidden="true"
                  >
                    {describeReminderMarkLabel(step.daysRelativeToDue)}
                  </span>
                </div>
              </TooltipTrigger>
              <ReminderMarkTooltipContent
                kind={kind}
                detail={describeReminderOffsetSentence(step.daysRelativeToDue)}
                hint="Drag, or use the arrow keys, to change the day"
              />
            </Tooltip>
          )
        })}
      </div>
    </div>
  )
}
